import type { DefaultStorage } from "@urql/exchange-graphcache/default-storage";
import { makeDefaultStorage } from "@urql/exchange-graphcache/default-storage";
import { isEmpty } from "lodash";
import { addCacheBreadcrumb, captureException } from "~/config/sentry";

const debug = false;

export const makeLocalStorage = () => {
  const cache = {};

  return {
    onOnline(cb: () => void) {
      window.addEventListener("online", () => {
        cb();
      });
    },
    readData() {
      return Promise.resolve().then(() => {
        const local = localStorage.getItem("data") ?? "{}";
        Object.assign(cache, JSON.parse(local));
        return cache;
      });
    },
    readMetadata() {
      return Promise.resolve().then(() => {
        const metadataJson = localStorage.getItem("metadata") ?? null;
        if (metadataJson === null) {
          return null;
        }
        // No access to the specific type from urql
        // eslint-disable-next-line
        return JSON.parse(metadataJson) as any[];
      });
    },
    writeData(delta: any) {
      return Promise.resolve().then(() => {
        Object.assign(cache, delta);
        localStorage.setItem("data", JSON.stringify(cache));
      });
    },
    writeMetadata(data: any) {
      localStorage.setItem("metadata", JSON.stringify(data));
    },
  };
};

/**
 * We override the default indexedDbStorage
 * to use it in a less intensive ressources consuming.
 * We are adding a layer to use native object to store the cache when initialised.
 * Writing data to indexedDb is bufferered to write into it every x seconds
 *
 * No need to add a cache layerfor write/read metadatas as it's called only one time
 */
const BUFFER_WRITING_TIME = 3000;
export const makeIndexedDbStorage = (): DefaultStorage => {
  const defaultStorage = makeDefaultStorage({
    idbName: "clovis", // The name of the IndexedDB database
    maxAge: 7, // The maximum age of the persisted data in days
  });

  type CachedDataType = Record<string, any>;
  let cachedData: CachedDataType | null = null;
  let bufferDeltas: CachedDataType = {};

  setInterval(() => {
    if (!isEmpty(bufferDeltas)) {
      debug && console.log("storage:bufferDeltas", bufferDeltas);
      defaultStorage.writeData(bufferDeltas).catch((error) => {
        // Capture IndexedDB write errors
        captureException(error, {
          extra: {
            context: "IndexedDB buffered write failed",
            deltaKeys: Object.keys(bufferDeltas).length,
          },
          level: "error",
        });
        addCacheBreadcrumb({
          category: "cache.storage",
          data: {
            deltaKeys: Object.keys(bufferDeltas).length,
            error: String(error),
          },
          isError: true,
          message: "IndexedDB buffered write failed",
        });
      });
      bufferDeltas = {};
    }
  }, BUFFER_WRITING_TIME);

  return {
    ...defaultStorage,
    readData: () => {
      if (!cachedData) {
        return defaultStorage
          .readData()
          .then((data) => {
            cachedData = data as CachedDataType;

            // Breadcrumb for successful cache initialization
            addCacheBreadcrumb({
              category: "cache.storage",
              data: { entriesCount: Object.keys(cachedData || {}).length },
              message: "IndexedDB cache initialized",
            });

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return data as any;
          })
          .catch((error) => {
            // Capture IndexedDB read errors (critical for debugging cache issues)
            captureException(error, {
              extra: {
                context: "IndexedDB readData failed",
              },
              level: "error",
            });
            addCacheBreadcrumb({
              category: "cache.storage",
              data: { error: String(error) },
              isError: true,
              message: "IndexedDB readData failed",
            });
            throw error;
          });
      }

      return Promise.resolve().then(() => {
        debug && console.log("storage:cachedData", cachedData);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return cachedData as any;
      });
    },
    readMetadata: () => {
      return defaultStorage.readMetadata!()
        .then((metadata) => {
          if (metadata && metadata.length > 0) {
            addCacheBreadcrumb({
              category: "cache.storage",
              data: { count: metadata.length },
              message: "Offline mutations metadata loaded",
            });
          }
          return metadata;
        })
        .catch((error) => {
          captureException(error, {
            extra: { context: "IndexedDB readMetadata failed" },
            level: "error",
          });
          addCacheBreadcrumb({
            category: "cache.storage",
            data: { error: String(error) },
            isError: true,
            message: "IndexedDB readMetadata failed",
          });
          throw error;
        });
    },
    writeData(delta: Record<string, any>) {
      debug && console.log("storage:writeData", delta);
      bufferDeltas = {
        ...bufferDeltas,
        ...delta,
      };

      cachedData = {
        ...(cachedData ?? {}),
        ...delta,
      };

      return Promise.resolve().then(() => {
        return cachedData;
      });
    },
    writeMetadata: (data: any) => {
      addCacheBreadcrumb({
        category: "cache.storage",
        data: { count: Array.isArray(data) ? data.length : 0 },
        message: "Offline mutations metadata saved",
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return defaultStorage.writeMetadata!(data);
    },
  };
};
